// Motor del chat de Atlas: arma el pedido, pega contra el agente, parsea el
// stream SSE y corre el loop de herramientas.
//
// Patrón imitado de `useV2Chat` (POST al agente → stream SSE bloque por bloque
// → si hay tool_use, se ejecutan y se vuelve a llamar, acotado por un tope de
// rondas), pero escrito de cero y MUCHO más chico: este chat no genera nada ni
// muestra tarjetas que esperan al usuario, así que las tools se resuelven solas.
// NO importa nada de `useV2Chat`: es otro dominio.
//
// Toda la lógica pura vive al lado (pedidoChatAtlas / streamChatAtlas /
// herramientasChatAtlas / promptChatAtlas) y está testeada; acá queda el
// cableado con React y la red.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AGENT_CHAT_URL, SUPABASE_ANON_KEY } from '../config';
import { supabase } from '../lib/supabase';
import {
  fetchBrandContext,
  fetchMemories,
  type AgentMemory,
  type BrandContext,
} from '../pages/v2/agentMemoryApi';
import {
  herramientasParaInterruptores,
  normalizarGrafico,
  normalizarSugerencias,
  resultadoHerramienta,
  NOMBRE_HERRAMIENTA_GRAFICA,
  NOMBRE_HERRAMIENTA_SUGERENCIAS,
} from './herramientasChatAtlas';
import {
  construirCuerpoPedido,
  corteConLimiteAlcanzado,
  corteSinFinal,
  debeSeguirElLoop,
  historialParaApi,
  INTERRUPTORES_POR_DEFECTO,
  MAX_RONDAS_HERRAMIENTAS,
  MODELO_POR_DEFECTO,
} from './pedidoChatAtlas';
import { esBurbujaVacia, hiloConError, mensajesParaCache } from './mensajesChat';
import { construirPromptChatAtlas } from './promptChatAtlas';
import { LectorStreamChatAtlas } from './streamChatAtlas';
import { contenidoUsuario } from './adjuntosChatAtlas';
import type {
  AdjuntoChat,
  ContextoApertura,
  GraficoChat,
  InterruptoresChat,
  MensajeApi,
  MensajeChatAtlas,
} from './tipos';

// ── Persistencia (sessionStorage) ─────────────────────────────────────────
// El chat NO se guarda en la tabla `agent_chats`: esa la lista el chat de V2
// SIN discriminador (agentChatApi.listChats hace un select sin filtro), así que
// cualquier fila nuestra aparecería mezclada en SU historial, que está en
// producción. Por ahora vive en memoria + un caché de sesión para sobrevivir un
// refresh o un cambio de pantalla. Ver ADR-2026-08-17-chat-atlas-motor-y-persistencia.
const CLAVE_MENSAJES = 'atlas-chat-atlas-mensajes';
const CLAVE_INTERRUPTORES = 'atlas-chat-atlas-interruptores';

/** Cuánto se espera a que el chorro de tokens pare antes de escribir el caché.
 *  400ms: bien por encima del ritmo de un stream y bien por debajo de lo que
 *  tarda alguien en refrescar la página a propósito. */
const MS_GUARDADO_CACHE = 400;

/**
 * Lo que se guarda en el caché de sesión: los mensajes MÁS de quién son.
 *
 * EL SELLO DE USUARIO NO ES DECORATIVO. `sessionStorage` sobrevive un refresh y
 * el logout de la app es 100% cliente (`signOut()` + `navigate('/auth')`, sin
 * recargar la página), así que con una clave suelta la conversación de una
 * cuenta reaparecía tal cual en la siguiente que entrara EN LA MISMA PESTAÑA.
 * Ver ADR-2026-08-17-chat-atlas-corte-por-cuenta.
 */
interface CacheMensajes {
  usuario: string | null;
  mensajes: MensajeChatAtlas[];
}

const leerCacheMensajes = (): CacheMensajes => {
  try {
    const crudo = sessionStorage.getItem(CLAVE_MENSAJES);
    if (!crudo) return { usuario: null, mensajes: [] };
    const parseado = JSON.parse(crudo) as unknown;
    // Formato viejo (array pelado, sin sello): se lee para no perder la
    // conversación de quien tenga la pestaña abierta durante el deploy, pero
    // entra SIN dueño — y sin dueño el chequeo de abajo lo descarta apenas
    // sepamos quién está logueado.
    if (Array.isArray(parseado)) {
      return { usuario: null, mensajes: parseado as MensajeChatAtlas[] };
    }
    if (
      parseado &&
      typeof parseado === 'object' &&
      Array.isArray((parseado as CacheMensajes).mensajes)
    ) {
      const p = parseado as CacheMensajes;
      return {
        usuario: typeof p.usuario === 'string' ? p.usuario : null,
        mensajes: p.mensajes,
      };
    }
    return { usuario: null, mensajes: [] };
  } catch {
    return { usuario: null, mensajes: [] };
  }
};

const leerInterruptoresGuardados = (): InterruptoresChat => {
  try {
    const crudo = sessionStorage.getItem(CLAVE_INTERRUPTORES);
    if (!crudo) return { ...INTERRUPTORES_POR_DEFECTO };
    const p = JSON.parse(crudo) as Partial<InterruptoresChat>;
    return {
      investigacion: p.investigacion === true,
      busquedaWeb: p.busquedaWeb !== false,
      respuestasGraficas: p.respuestasGraficas !== false,
    };
  } catch {
    return { ...INTERRUPTORES_POR_DEFECTO };
  }
};

const nuevoId = (): string => {
  try {
    return crypto.randomUUID();
  } catch {
    return `m-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }
};

/** Corre una promesa con tope de tiempo. Sin esto, un `getSession` colgado
 *  (falla conocida de Supabase cuando el refresh se traba) deja el chat
 *  "pensando" para siempre, porque pasa ANTES del watchdog del stream. */
function conTope<T>(p: Promise<T>, ms: number, etiqueta: string): Promise<T> {
  return new Promise<T>((resolver, rechazar) => {
    const t = setTimeout(() => rechazar(new Error(`timeout:${etiqueta} (${ms}ms)`)), ms);
    p.then(
      (v) => {
        clearTimeout(t);
        resolver(v);
      },
      (e) => {
        clearTimeout(t);
        rechazar(e);
      },
    );
  });
}

const TEXTO_ERROR = 'No pude conectarme. Probá de nuevo en un toque.';
const TEXTO_SIN_VUELTAS =
  'Me quedé sin vueltas para resolver eso. Contame de otra forma y lo encaro de nuevo.';
/** El stream se cortó antes de que llegara una sola palabra. */
const TEXTO_CORTADO = 'Se cortó la respuesta antes de empezar. Probá de nuevo.';

export interface MotorChatAtlas {
  mensajes: MensajeChatAtlas[];
  streaming: boolean;
  /** Etiqueta de la herramienta server-side en curso (o null). */
  actividad: string | null;
  interruptores: InterruptoresChat;
  setInterruptor: (clave: keyof InterruptoresChat, valor: boolean) => void;
  /** Envía un mensaje. `contexto` describe desde qué pantalla se está usando;
   *  `adjuntos` son los archivos que el usuario enganchó con el menú "+". */
  enviarMensaje: (
    texto: string,
    contexto?: ContextoApertura | null,
    adjuntos?: AdjuntoChat[],
  ) => void;
  /** Corta el turno en curso sin borrar la conversación. */
  detener: () => void;
  /** Borra la conversación y arranca de cero. */
  reiniciar: () => void;
}

export function useMotorChatAtlas(): MotorChatAtlas {
  // El caché se lee UNA vez, y con él viene de quién era: el sello lo verifica
  // el efecto de "corte por cuenta" de más abajo.
  const cacheInicialRef = useRef<CacheMensajes | null>(null);
  if (cacheInicialRef.current === null) cacheInicialRef.current = leerCacheMensajes();
  const [mensajes, setMensajes] = useState<MensajeChatAtlas[]>(
    () => cacheInicialRef.current?.mensajes ?? [],
  );
  const [streaming, setStreaming] = useState(false);
  const [actividad, setActividad] = useState<string | null>(null);
  const [interruptores, setInterruptores] = useState<InterruptoresChat>(
    leerInterruptoresGuardados,
  );

  // Espejos para leer dentro del turno sin recrear callbacks ni arrastrar
  // closures viejos.
  const mensajesRef = useRef<MensajeChatAtlas[]>(mensajes);
  mensajesRef.current = mensajes;
  const interruptoresRef = useRef<InterruptoresChat>(interruptores);
  interruptoresRef.current = interruptores;
  const streamingRef = useRef(false);

  // Token de generación: `detener`/`reiniciar` lo incrementan y todo turno en
  // vuelo se da por muerto al chequearlo después de cada await.
  const generacionRef = useRef(0);
  const abortRef = useRef<AbortController | null>(null);
  const identidadRef = useRef<{ userId: string | null; idCompany: string | null } | null>(
    null,
  );
  const marcaRef = useRef<BrandContext | null>(null);
  const memoriasRef = useRef<AgentMemory[]>([]);
  // Dueño de lo que hay en memoria y en el caché. Arranca con el sello del
  // caché y lo confirma (o lo desmiente) el efecto de corte por cuenta.
  const usuarioRef = useRef<string | null>(cacheInicialRef.current?.usuario ?? null);

  // Caché de sesión (sobrevive refresh / navegación, no la pestaña).
  //
  // CON RETARDO, Y NO EN CADA COMMIT. `agregarTexto` hace un `setMensajes` por
  // cada fragmento del stream, así que un efecto directo sobre `[mensajes]`
  // corría `JSON.stringify` + `setItem` —las dos síncronas, en el hilo
  // principal— decenas de veces por segundo mientras Atlas escribe, con un
  // costo que crece con el largo de la conversación. Con el retardo se guarda
  // una vez cuando el chorro para. Lo que se paga: si el usuario refresca
  // dentro de esa ventana, pierde el último tramo — mucho menos grave que
  // trabar la UI justo cuando está mirando.
  useEffect(() => {
    const id = setTimeout(() => {
      try {
        if (mensajes.length === 0) sessionStorage.removeItem(CLAVE_MENSAJES);
        else
          sessionStorage.setItem(
            CLAVE_MENSAJES,
            JSON.stringify({
              usuario: usuarioRef.current,
              // Sin el base64 de los adjuntos: ver `mensajesParaCache`.
              mensajes: mensajesParaCache(mensajes),
            }),
          );
      } catch {
        // Cuota llena o storage bloqueado. El chat sigue entero EN MEMORIA; lo
        // que se pierde es sobrevivir un refresh. Borramos la clave a
        // propósito: si quedara la versión vieja, un refresh restauraría una
        // conversación TRUNCADA — peor que arrancar limpio, porque parece
        // completa.
        try {
          sessionStorage.removeItem(CLAVE_MENSAJES);
        } catch {
          /* noop */
        }
      }
    }, MS_GUARDADO_CACHE);
    return () => clearTimeout(id);
  }, [mensajes]);

  useEffect(() => {
    try {
      sessionStorage.setItem(CLAVE_INTERRUPTORES, JSON.stringify(interruptores));
    } catch {
      /* noop */
    }
  }, [interruptores]);

  const setInterruptor = useCallback(
    (clave: keyof InterruptoresChat, valor: boolean) => {
      setInterruptores((prev) => ({ ...prev, [clave]: valor }));
    },
    [],
  );

  // ── helpers de mensajes ────────────────────────────────────────────────

  const agregarTexto = useCallback((idMensaje: string, pedazo: string) => {
    setMensajes((prev) =>
      prev.map((m) => (m.id === idMensaje ? { ...m, texto: m.texto + pedazo } : m)),
    );
  }, []);

  const parchear = useCallback(
    (idMensaje: string, parche: Partial<MensajeChatAtlas>) => {
      setMensajes((prev) =>
        prev.map((m) => (m.id === idMensaje ? { ...m, ...parche } : m)),
      );
    },
    [],
  );

  // ── contexto del usuario (identidad + marca + memorias) ────────────────

  /** Tira a la basura TODO lo que depende de quién está logueado. Se llama al
   *  desloguear y al detectar que la sesión cambió de dueño. */
  const olvidarIdentidad = useCallback(() => {
    identidadRef.current = null;
    marcaRef.current = null;
    memoriasRef.current = [];
  }, []);

  const cargarContexto = useCallback(async () => {
    try {
      // La identidad se latchea una vez por pestaña, así que hay que
      // desconfiar de ella si la sesión cambió de dueño en el medio: con el
      // `userId`/`idCompany` viejos, `fetchBrandContext` traería la marca de la
      // cuenta anterior y `construirPromptChatAtlas` la metería en el prompt
      // del usuario nuevo.
      if (
        identidadRef.current &&
        usuarioRef.current !== null &&
        identidadRef.current.userId !== usuarioRef.current
      ) {
        olvidarIdentidad();
      }
      if (!identidadRef.current) {
        const {
          data: { user },
        } = await conTope(supabase.auth.getUser(), 10_000, 'getUser');
        let idCompany: string | null = null;
        if (user) {
          // El query builder de Supabase es "thenable", no una Promise: lo
          // envolvemos para poder ponerle tope de tiempo.
          const respuesta = await conTope(
            (async () =>
              await supabase
                .from('profiles')
                .select('id_company')
                .eq('id', user.id)
                .maybeSingle())(),
            10_000,
            'perfil',
          );
          const perfil = respuesta.data as { id_company?: string | null } | null;
          idCompany = perfil?.id_company ?? null;
        }
        identidadRef.current = { userId: user?.id ?? null, idCompany };
      }
      const { userId, idCompany } = identidadRef.current;
      if (!idCompany) return;
      const [marca, memorias] = await conTope(
        Promise.all([fetchBrandContext(userId, idCompany), fetchMemories(idCompany)]),
        12_000,
        'contexto',
      );
      marcaRef.current = marca;
      memoriasRef.current = memorias;
    } catch {
      // Sin contexto el chat igual funciona (con lo que haya quedado cacheado).
    }
  }, [olvidarIdentidad]);

  // ── envío ──────────────────────────────────────────────────────────────

  const enviarMensaje = useCallback(
    (
      textoCrudo: string,
      contexto?: ContextoApertura | null,
      adjuntos?: AdjuntoChat[],
    ) => {
      const texto = textoCrudo.trim();
      const conAdjuntos = (adjuntos?.length ?? 0) > 0;
      // Solo adjuntos y sin texto SÍ es un turno válido (mandar una foto y
      // preguntar después); lo que no vale es un turno completamente vacío.
      if ((!texto && !conAdjuntos) || streamingRef.current) return;

      const genToken = generacionRef.current;
      const vivo = () => genToken === generacionRef.current;

      const mensajesApi: MensajeApi[] = historialParaApi(mensajesRef.current);
      mensajesApi.push({ role: 'user', content: contenidoUsuario(texto, adjuntos) });

      setMensajes((prev) => [
        ...prev,
        {
          id: nuevoId(),
          rol: 'user',
          texto,
          ...(conAdjuntos ? { adjuntos } : {}),
        },
      ]);
      streamingRef.current = true;
      setStreaming(true);

      void (async () => {
        try {
          await cargarContexto();
          if (!vivo()) return;

          const { estable, volatil } = construirPromptChatAtlas({
            marca: marcaRef.current,
            memorias: memoriasRef.current,
            interruptores: interruptoresRef.current,
            contexto: contexto ?? null,
          });
          const herramientas = herramientasParaInterruptores(interruptoresRef.current);

          for (let ronda = 0; ronda < MAX_RONDAS_HERRAMIENTAS; ronda++) {
            if (!vivo()) return;

            const {
              data: { session },
            } = await conTope(supabase.auth.getSession(), 15_000, 'getSession');
            if (!session?.access_token) throw new Error('sin sesión');
            if (!vivo()) return;

            // Watchdog de inactividad: si el stream queda mudo (compu dormida,
            // red caída, backend colgado) abortamos en vez de quedar
            // "pensando" para siempre. El backend manda `: ping` cada 15s
            // (keepalive), así que el silencio real es corto; mientras corre
            // una tool server-side (un scraper puede tardar minutos) damos aire.
            const ac = new AbortController();
            abortRef.current = ac;
            let hayActividad = false;
            let temporizador: ReturnType<typeof setTimeout> | null = null;
            const rearmar = () => {
              if (temporizador) clearTimeout(temporizador);
              temporizador = setTimeout(() => ac.abort(), hayActividad ? 240_000 : 90_000);
            };
            rearmar();

            const res = await fetch(AGENT_CHAT_URL, {
              method: 'POST',
              signal: ac.signal,
              headers: {
                Authorization: `Bearer ${session.access_token}`,
                apikey: SUPABASE_ANON_KEY,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(
                construirCuerpoPedido({
                  mensajesApi,
                  herramientas,
                  system: estable,
                  systemVolatil: volatil,
                  interruptores: interruptoresRef.current,
                  modelo: MODELO_POR_DEFECTO,
                  idCompany: identidadRef.current?.idCompany ?? null,
                }),
              ),
            }).catch((e) => {
              if (temporizador) clearTimeout(temporizador);
              throw e;
            });

            if (!res.ok || !res.body) {
              if (temporizador) clearTimeout(temporizador);
              const detalle = await res.json().catch(() => null);
              const mensajeError =
                (detalle as { error?: string } | null)?.error ||
                `El agente respondió ${res.status}`;
              throw new Error(mensajeError);
            }

            // Burbuja del asistente para esta ronda (el texto se pinta ahí a
            // medida que llega).
            const idAsistente = nuevoId();
            setMensajes((prev) => [
              ...prev,
              { id: idAsistente, rol: 'assistant', texto: '' },
            ]);

            const lector = new LectorStreamChatAtlas({
              onTexto: (pedazo) => {
                if (vivo()) agregarTexto(idAsistente, pedazo);
              },
              onActividad: (etiqueta) => {
                hayActividad = etiqueta !== null;
                if (vivo()) setActividad(etiqueta);
              },
            });

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            while (true) {
              const { done, value } = await reader.read();
              rearmar(); // cualquier byte (incluido un ping) resetea el watchdog
              if (done) break;
              lector.alimentar(decoder.decode(value, { stream: true }));
            }
            if (temporizador) clearTimeout(temporizador);
            lector.finalizar();
            if (!vivo()) return;
            setActividad(null);

            const usadas = lector.herramientasUsadas();

            const estadoRonda = {
              stopReason: lector.stopReason,
              herramientas: usadas.length,
              ronda,
            };

            // Efectos locales de las tools (guardar gráfico / sugerencias).
            const graficos: GraficoChat[] = [];
            let sugerencias: string[] = [];
            for (const h of usadas) {
              if (h.nombre === NOMBRE_HERRAMIENTA_GRAFICA) {
                const g = normalizarGrafico(h.entrada);
                if (g) graficos.push({ id: h.id || nuevoId(), titulo: g.titulo, html: g.html });
              } else if (h.nombre === NOMBRE_HERRAMIENTA_SUGERENCIAS) {
                const s = normalizarSugerencias(h.entrada);
                if (s.length > 0) sugerencias = s;
              }
            }

            // ── Qué queda en la burbuja de esta ronda ──────────────────
            // Se decide UNA sola vez y mirando lo que se va a DIBUJAR, no
            // cuántas herramientas se llamaron: una ronda puede invocar una
            // tool que no existe (o mandarle un input inválido a la gráfica) y
            // no dejar nada en pantalla.
            const seguir = debeSeguirElLoop(estadoRonda);
            const parche: Partial<MensajeChatAtlas> = {};
            if (graficos.length > 0) parche.graficos = graficos;
            if (sugerencias.length > 0) parche.sugerencias = sugerencias;

            if (!seguir && corteConLimiteAlcanzado(estadoRonda) && !lector.texto) {
              parche.texto = TEXTO_SIN_VUELTAS;
            } else if (corteSinFinal(estadoRonda)) {
              // El stream cerró limpio pero sin `stop_reason`: la respuesta
              // está cortada. Con texto se marca (y se sigue leyendo); sin
              // nada que mostrar, el turno terminaría MUDO, así que avisa.
              if (lector.texto) parche.truncado = true;
              else if (!parche.graficos && !parche.sugerencias) {
                parche.texto = TEXTO_CORTADO;
                parche.error = true;
              }
            }

            const burbujaVacia =
              !lector.texto && !parche.texto && !parche.graficos && !parche.sugerencias;
            if (burbujaVacia) {
              // Sin nada que dibujar la sacamos: si no, queda un hueco mudo en
              // el hilo (y una burbuja más por cada vuelta del loop).
              setMensajes((prev) => prev.filter((m) => m.id !== idAsistente));
            } else if (Object.keys(parche).length > 0) {
              parchear(idAsistente, parche);
            }

            if (!seguir) break;

            // Otra vuelta: el historial de la API se lleva los bloques crudos
            // del asistente + los resultados de cada tool.
            mensajesApi.push({
              role: 'assistant',
              content: lector.bloquesOrdenados().map((b) =>
                b.tipo === 'texto'
                  ? { type: 'text', text: b.texto }
                  : { type: 'tool_use', id: b.id, name: b.nombre, input: b.entrada },
              ),
            });
            mensajesApi.push({
              role: 'user',
              content: usadas.map((h) => ({
                type: 'tool_result',
                tool_use_id: h.id,
                content: JSON.stringify(resultadoHerramienta(h.nombre, h.entrada)),
              })),
            });
          }
        } catch (err) {
          if (!vivo()) return; // lo cortó el usuario: no es un error
          console.error('[chat-atlas] enviarMensaje', err);
          setMensajes((prev) => hiloConError(prev, TEXTO_ERROR, nuevoId));
        } finally {
          if (vivo()) {
            streamingRef.current = false;
            setStreaming(false);
            setActividad(null);
          }
          // El backend descuenta los créditos del turno apenas cierra el
          // stream: avisamos para que el saldo del chrome se refresque.
          try {
            window.dispatchEvent(new Event('atlas:credits-updated'));
          } catch {
            /* noop */
          }
        }
      })();
    },
    [agregarTexto, cargarContexto, parchear],
  );

  const detener = useCallback(() => {
    generacionRef.current += 1;
    try {
      abortRef.current?.abort();
    } catch {
      /* noop */
    }
    abortRef.current = null;
    streamingRef.current = false;
    setStreaming(false);
    setActividad(null);
    // Cortar antes del primer token dejaba en pie la burbuja que la ronda crea
    // por adelantado: un hueco vacío en el hilo (y encima persistido). El turno
    // ya está dado por muerto (`generacionRef`), así que nadie más la va a
    // tocar.
    setMensajes((prev) => {
      const ultimo = prev[prev.length - 1];
      return ultimo && esBurbujaVacia(ultimo) ? prev.slice(0, -1) : prev;
    });
  }, []);

  const reiniciar = useCallback(() => {
    detener();
    setMensajes([]);
    // La marca y las memorias se vuelven a pedir en el próximo turno (
    // `cargarContexto` las refresca siempre), así que soltarlas no cuesta una
    // llamada de más y garantiza que "empezar de cero" no arrastre el contexto
    // de una empresa anterior. La IDENTIDAD sí se conserva: es la misma cuenta,
    // y de un cambio de cuenta se encarga el corte de abajo.
    marcaRef.current = null;
    memoriasRef.current = [];
    try {
      sessionStorage.removeItem(CLAVE_MENSAJES);
    } catch {
      /* noop */
    }
  }, [detener]);

  // ── Corte por cuenta ───────────────────────────────────────────────────
  //
  // El provider vive ARRIBA de `<Routes>` y nunca se desmonta, y el logout de
  // la app es puramente cliente (`signOut()` + `navigate('/auth')`, sin
  // recargar). Sin esto, al entrar otra cuenta en la misma pestaña quedaban
  // vivas tres capas de la anterior: los mensajes en memoria, el caché de
  // sesión (que además un refresh restauraba) y —la peor— la identidad, la
  // marca y las memorias, que `construirPromptChatAtlas` inyecta en el system
  // prompt de cada turno. Ver ADR-2026-08-17-chat-atlas-corte-por-cuenta.
  const olvidarTodo = useCallback(() => {
    detener();
    olvidarIdentidad();
    setMensajes([]);
    try {
      sessionStorage.removeItem(CLAVE_MENSAJES);
    } catch {
      /* noop */
    }
  }, [detener, olvidarIdentidad]);

  useEffect(() => {
    let vivoEfecto = true;

    // Al montar: ¿el caché que acabamos de restaurar es de quien está logueado
    // ahora? `getSession` es lectura local (no pega a la red), así que esto no
    // agrega latencia al arranque.
    void (async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!vivoEfecto) return;
        const actual = session?.user?.id ?? null;
        if (actual !== usuarioRef.current) {
          usuarioRef.current = actual;
          olvidarTodo();
        }
      } catch {
        /* sesión ilegible: se deja como está */
      }
    })();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((evento, session) => {
      const actual = session?.user?.id ?? null;
      if (evento === 'SIGNED_OUT') {
        usuarioRef.current = null;
        olvidarTodo();
        return;
      }
      if (!actual) return;
      // `TOKEN_REFRESHED` y compañía llegan con el MISMO usuario: ahí no se
      // toca nada. Sólo un id distinto es un cambio de cuenta.
      if (usuarioRef.current !== null && usuarioRef.current !== actual) olvidarTodo();
      usuarioRef.current = actual;
    });

    return () => {
      vivoEfecto = false;
      subscription.unsubscribe();
    };
  }, [olvidarTodo]);

  // El objeto se MEMOIZA: es la dependencia del `useMemo` del provider, así que
  // devolver un literal nuevo en cada render dejaba ese memo (y los
  // `useCallback` que dependen de él) sin efecto, y con eso el valor del
  // contexto cambiaba de identidad en cada token.
  return useMemo(
    () => ({
      mensajes,
      streaming,
      actividad,
      interruptores,
      setInterruptor,
      enviarMensaje,
      detener,
      reiniciar,
    }),
    [
      mensajes,
      streaming,
      actividad,
      interruptores,
      setInterruptor,
      enviarMensaje,
      detener,
      reiniciar,
    ],
  );
}
